import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  Target,
  Search,
  DollarSign,
  Tag,
  FileText,
  Check,
  X,
  ChevronDown,
  AlertTriangle,
} from 'lucide-react';

interface Competitor {
  id: string;
  name: string;
  product_count: number;
}

interface CompetitorProduct {
  id: string;
  name: string;
  sku?: string;
}

interface PriceRecord {
  id: string;
  competitor_name?: string;
  product_name?: string;
  price: number;
  promo_price?: number;
  notes?: string;
  created_at: string;
}

const PROMO_TYPES = [
  { value: 'DISCOUNT', label: 'Desconto' },
  { value: 'BUNDLE', label: 'Combo / Bundle' },
  { value: 'DISPLAY', label: 'Display Especial' },
  { value: 'SAMPLING', label: 'Degustacao' },
  { value: 'BOGO', label: 'Leve e Pague' },
];

const IMPACT_LEVELS = [
  { value: 'LOW', label: 'Baixo' },
  { value: 'MEDIUM', label: 'Medio' },
  { value: 'HIGH', label: 'Alto' },
  { value: 'CRITICAL', label: 'Critico' },
];

export default function CompetitorCollection({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  // Form state
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<CompetitorProduct | null>(null);
  const [price, setPrice] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [promoPrice, setPromoPrice] = useState('');
  const [promoType, setPromoType] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [impactLevel, setImpactLevel] = useState('');
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dropdown visibility
  const [showCompetitorDropdown, setShowCompetitorDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Fetch competitors
  const { data: competitors = [] } = useQuery<Competitor[]>({
    queryKey: ['competitors', 'active'],
    queryFn: () => api.get('/competitors?is_active=true&limit=100').then(res => res.data.data),
  });

  // Fetch competitor products (enabled when competitor is selected)
  const { data: competitorProducts = [] } = useQuery<CompetitorProduct[]>({
    queryKey: ['competitor-products', selectedCompetitor?.id],
    queryFn: () =>
      api.get(`/competitors/${selectedCompetitor!.id}/products?is_active=true&limit=100`).then(res => res.data.data),
    enabled: !!selectedCompetitor,
  });

  // Fetch existing price records for this visit
  const { data: existingRecords = [] } = useQuery<PriceRecord[]>({
    queryKey: ['benchmark-prices', visitId],
    queryFn: () => api.get(`/benchmark/prices?visit_id=${visitId}&limit=20`).then(res => res.data.data),
  });

  // Submit price mutation
  const priceMutation = useMutation({
    mutationFn: async () => {
      // Submit price record
      await api.post('/benchmark/prices', {
        visit_id: visitId,
        store_id: storeId,
        competitor_product_id: selectedProduct!.id,
        price: Number(price),
        promo_price: promoPrice ? Number(promoPrice) : undefined,
        notes: notes || undefined,
      });

      // If promo data is filled, also submit promo record
      if (showPromo && promoType && impactLevel) {
        await api.post('/benchmark/promos', {
          visit_id: visitId,
          store_id: storeId,
          competitor_id: selectedCompetitor!.id,
          type: promoType,
          discount_percent: discountPercent ? Number(discountPercent) : undefined,
          impact_level: impactLevel,
        });
      }
    },
    onSuccess: () => {
      setSuccessMessage('Dados competitivos registrados com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['benchmark-prices', visitId] });
      // Reset form
      setSelectedCompetitor(null);
      setSelectedProduct(null);
      setPrice('');
      setShowPromo(false);
      setPromoPrice('');
      setPromoType('');
      setDiscountPercent('');
      setImpactLevel('');
      setNotes('');
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao registrar dados competitivos');
    },
  });

  const handleSubmit = () => {
    if (!selectedCompetitor) {
      alert('Selecione um concorrente');
      return;
    }
    if (!selectedProduct) {
      alert('Selecione um produto');
      return;
    }
    if (!price || Number(price) <= 0) {
      alert('Informe o preco');
      return;
    }
    priceMutation.mutate();
  };

  const handleSelectCompetitor = (competitor: Competitor) => {
    setSelectedCompetitor(competitor);
    setSelectedProduct(null); // Reset product when competitor changes
    setShowCompetitorDropdown(false);
  };

  const handleSelectProduct = (product: CompetitorProduct) => {
    setSelectedProduct(product);
    setShowProductDropdown(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-orange-600 text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Coleta Competitiva</h1>
          </div>
          <Target className="h-6 w-6 opacity-80" />
        </div>
      </header>

      {/* Success Toast */}
      {successMessage && (
        <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 p-4 space-y-6 pb-8">
        {/* Select Competitor */}
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
            <Target className="h-4 w-4 text-orange-600" />
            Concorrente *
          </h3>

          {selectedCompetitor ? (
            <div className="bg-white border border-orange-300 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedCompetitor.name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedCompetitor.product_count} produto(s)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedCompetitor(null);
                    setSelectedProduct(null);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={() => setShowCompetitorDropdown(!showCompetitorDropdown)}
                className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <span>Selecionar concorrente...</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showCompetitorDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {competitors.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Nenhum concorrente cadastrado
                    </div>
                  ) : (
                    competitors.map((competitor) => (
                      <button
                        key={competitor.id}
                        onClick={() => handleSelectCompetitor(competitor)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{competitor.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {competitor.product_count} produto(s)
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Select Product */}
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
            <Search className="h-4 w-4 text-orange-600" />
            Produto do Concorrente *
          </h3>

          {selectedProduct ? (
            <div className="bg-white border border-orange-300 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                  {selectedProduct.sku && (
                    <p className="text-sm text-gray-500 mt-1">SKU: {selectedProduct.sku}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={() => {
                  if (selectedCompetitor) setShowProductDropdown(!showProductDropdown);
                }}
                disabled={!selectedCompetitor}
                className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <span>{selectedCompetitor ? 'Selecionar produto...' : 'Selecione um concorrente primeiro'}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showProductDropdown && selectedCompetitor && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {competitorProducts.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Nenhum produto cadastrado para este concorrente
                    </div>
                  ) : (
                    competitorProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        {product.sku && (
                          <p className="text-xs text-gray-500 mt-0.5">SKU: {product.sku}</p>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Price Field */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
            <DollarSign className="h-4 w-4 text-orange-600" />
            Preco (R$) *
          </label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Ex: 12.90"
            min="0"
            step="0.01"
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        {/* Promo Price Toggle */}
        <div>
          <button
            onClick={() => setShowPromo(!showPromo)}
            className={`w-full flex items-center justify-between p-3 rounded-xl border text-sm font-medium transition-colors ${
              showPromo
                ? 'bg-orange-50 border-orange-300 text-orange-700'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              <span>Dados promocionais</span>
            </div>
            <div
              className={`w-10 h-6 rounded-full transition-colors flex items-center ${
                showPromo ? 'bg-orange-600 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-5 h-5 bg-white rounded-full shadow mx-0.5" />
            </div>
          </button>
        </div>

        {/* Promo Fields */}
        {showPromo && (
          <div className="space-y-4 bg-orange-50 rounded-xl border border-orange-200 p-4">
            <h4 className="text-sm font-semibold text-orange-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Informacoes Promocionais
            </h4>

            {/* Promo Price */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Preco Promocional (R$)
              </label>
              <input
                type="number"
                value={promoPrice}
                onChange={(e) => setPromoPrice(e.target.value)}
                placeholder="Ex: 9.90"
                min="0"
                step="0.01"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Promo Type */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Tipo de Promocao
              </label>
              <select
                value={promoType}
                onChange={(e) => setPromoType(e.target.value)}
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Selecionar tipo...</option>
                {PROMO_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Discount Percent */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Desconto (%)
              </label>
              <input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                placeholder="Ex: 15"
                min="0"
                max="100"
                step="0.1"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Impact Level */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Nivel de Impacto
              </label>
              <select
                value={impactLevel}
                onChange={(e) => setImpactLevel(e.target.value)}
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Selecionar impacto...</option>
                {IMPACT_LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
            <FileText className="h-4 w-4 text-orange-600" />
            Observacoes (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observacoes sobre o concorrente..."
            rows={3}
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={priceMutation.isPending || !selectedCompetitor || !selectedProduct || !price || Number(price) <= 0}
          className="w-full flex items-center justify-center gap-2 p-4 bg-orange-600 text-white rounded-xl font-semibold active:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {priceMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Registrando...</span>
            </>
          ) : (
            <span>Registrar Coleta</span>
          )}
        </button>

        {/* Existing Records List */}
        {existingRecords.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Registros Coletados ({existingRecords.length})
            </h3>
            <div className="space-y-2">
              {existingRecords.map((record) => (
                <div
                  key={record.id}
                  className="bg-white rounded-xl border border-gray-100 p-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-orange-100 mt-0.5">
                      <Target className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {record.product_name || 'Produto'}
                      </p>
                      {record.competitor_name && (
                        <p className="text-xs text-gray-500">{record.competitor_name}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-2 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-full">
                          R$ {Number(record.price).toFixed(2)}
                        </span>
                        {record.promo_price != null && (
                          <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                            Promo: R$ {Number(record.promo_price).toFixed(2)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(record.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {record.notes && (
                        <p className="text-xs text-gray-500 mt-1">{record.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
